import { useState } from "react";
import { usePersons } from "../../../../../../hooks/usePersons";
import { useToast } from "../../../../../../mantine/notifications/useToast";
import { VermarkterVollmachtUploadSuccessStatus } from "../../../../../../types/api.types";
import { getErrorText } from "../../../../../../utils/get-error-text";
import { useConfirmVollmachtVermarkter } from "./useConfirmVollmachtVermarkter";
import { useDownloadVollmachtVermarkter } from "./useDownloadVollmachtVermarkter";
import {
  useUploadVollmachtVermarkter,
  type VermarkterVollmachtUploadSuccess
} from "./useUploadVollmachtVermarkter";

const VMVOLLMACHT_TODO_CLOSED_SUCCESS_MESSAGE =
  "Die Vollmacht wurde erfolgreich hochgeladen. Bitte beachten Sie, dass bei vollständig bedienten Liegenschaften die Aufgabe geschlossen wurde. Auf noch fehlende Vollmachten werden Sie bei der Stufe hingewiesen.";

function useVollmachtVermarkterUpload(siteId: number) {
  const { showErrorToast, showToast } = useToast();
  const { persons } = usePersons({ siteId });
  const [confirmationData, setConfirmationData] =
    useState<VermarkterVollmachtUploadSuccess | null>(null);

  const { generateAndDownloadVollmachtVermarkterMutation } =
    useDownloadVollmachtVermarkter(siteId);
  const { uploadVollmachtVermarkterMutation } =
    useUploadVollmachtVermarkter(siteId);
  const { confirmVollmachtVermarkterMutation } =
    useConfirmVollmachtVermarkter(siteId);

  const personsWithEmAndRedispatch = persons.filter(
    (person) =>
      person.isErloesmonitoringActive && person.isRedispatchImportActive
  );

  function handleFilesSelected(personId: number, files: Array<File>) {
    uploadVollmachtVermarkterMutation.mutate(
      { files, personId },
      {
        onSuccess: (results) => {
          const uploadResponse = results[0]?.data;
          if (
            uploadResponse &&
            (uploadResponse.status ===
              VermarkterVollmachtUploadSuccessStatus.need_confirmation ||
              uploadResponse.status ===
                VermarkterVollmachtUploadSuccessStatus.rejected)
          ) {
            setConfirmationData(uploadResponse);
          } else if (
            uploadResponse?.status ===
            VermarkterVollmachtUploadSuccessStatus.valid
          ) {
            showToast({
              type: "success",
              message: VMVOLLMACHT_TODO_CLOSED_SUCCESS_MESSAGE,
              autoClose: false,
              withCloseButton: true
            });
            setConfirmationData(null);
          } else {
            setConfirmationData(null);
          }
        },
        onError: (error) => showErrorToast(getErrorText(error))
      }
    );
  }

  function handleConfirmVollmacht(userConfirmedExtractedData: boolean) {
    if (
      confirmationData?.status ===
      VermarkterVollmachtUploadSuccessStatus.rejected
    ) {
      showToast({
        type: "info",
        message: "Die Vollmacht wurde verworfen."
      });
      setConfirmationData(null);
      return;
    }

    if (!confirmationData?.todo_file) {
      showErrorToast("Die Vollmacht konnte nicht bestätigt werden.");
      return;
    }

    confirmVollmachtVermarkterMutation.mutate(
      {
        todoFileId: confirmationData.todo_file,
        userConfirmedExtractedData
      },
      {
        onSuccess: (response) => {
          const { data } = response;
          showToast(
            data.todo_closed
              ? {
                  type: "success",
                  message: VMVOLLMACHT_TODO_CLOSED_SUCCESS_MESSAGE,
                  autoClose: false,
                  withCloseButton: true
                }
              : {
                  type: userConfirmedExtractedData ? "success" : "info",
                  message: userConfirmedExtractedData
                    ? "Die Vollmacht wurde bestätigt."
                    : "Die Vollmacht wurde verworfen."
                }
          );
          setConfirmationData(null);
        },
        onError: (error) => showErrorToast(getErrorText(error))
      }
    );
  }

  function handleCloseConfirmation() {
    if (!confirmVollmachtVermarkterMutation.isPending) {
      setConfirmationData(null);
    }
  }

  return {
    confirmationData,
    confirmVollmachtVermarkterMutation,
    generateAndDownloadVollmachtVermarkterMutation,
    personsWithEmAndRedispatch,
    uploadVollmachtVermarkterMutation,
    handleCloseConfirmation,
    handleConfirmVollmacht,
    handleFilesSelected
  };
}

export { useVollmachtVermarkterUpload };
